import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Table, { SortableTh } from '../../components/Table';
import useSort from '../../components/Table/useSort';
import SelectBox from '../../components/SelectBox';
import Pagination from '../../components/Pagination';
import usePagination from '../../components/Pagination/usePagination';
import NetworkError from '../../components/Error';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import { getVulnerabilities } from '../../redux/actions/watcherActions';

import styles from './MorningWatch.module.css';

// MorningWatch: CVEs found on the client's OWN systems. The appliance agent
// reads product/version banners off the wire and the C2C matches them against
// its CVE corpus — this shows the resulting findings for the caller's tenant.

const MorningWatchScreen = () => {
	const dispatch = useDispatch();

	const [selectedWatcher, setSelectedWatcher] = React.useState('All Watchers');
	const [searchKey, setSearchKey] = React.useState('');
	// Deep-link from the dashboard: ?q=<domain> pre-fills the search.
	const location = useLocation();
	React.useEffect(() => {
		const q = new URLSearchParams(location.search).get('q');
		if (q) setSearchKey(q);
	}, [location.search]);
	const [loading, setLoading] = React.useState(false);
	const [watchers, setWatchers] = React.useState([]);
	const [findings, setFindings] = React.useState([]);
	const [idsEvents, setIdsEvents] = React.useState([]);
	const [summary, setSummary] = React.useState(null);
	const [hasError, setHasError] = React.useState(false);
	const [supportErrorMessage, setSupportErrorMessage] = React.useState('');

	React.useEffect(() => {
		let active = true;
		(async () => {
			setLoading(true);
			const data = await dispatch(getVulnerabilities(selectedWatcher));
			if (!active) return;
			if (data && data.status) {
				setFindings(Array.isArray(data.findings) ? data.findings : []);
				setIdsEvents(Array.isArray(data.idsEvents) ? data.idsEvents : []);
				setSummary(data.summary || null);
				if (Array.isArray(data.watchers)) setWatchers(data.watchers);
				setHasError(false);
				setSupportErrorMessage('');
			} else {
				setHasError(true);
				setSupportErrorMessage(formatFrontendSupportError({
					errorCode: ErrorCodes.API_INTERNAL_ERROR,
					pageInfo: 'MorningWatch Page / Findings Load Failed',
					sourceMessage: (data && data.msg) || 'Findings request did not return success',
				}));
			}
			setLoading(false);
		})();
		return () => { active = false; };
	}, [selectedWatcher, dispatch]);

	const filtered = React.useMemo(() => {
		const key = searchKey.trim().toLowerCase();
		if (!key) return findings;
		return findings.filter((f) =>
			[f.cve, f.product, f.host, f.watcher, f.severity].some(
				(v) => String(v).toLowerCase().includes(key)));
	}, [findings, searchKey]);

	const sort = useSort(filtered);
	const idsSort = useSort(idsEvents || []);
	const { pageRows, pageNumber, totalRows, pageLimit, handlePageChange } = usePagination(sort.sorted);

	const sevClass = (sev) => styles['sev_' + String(sev || '').toLowerCase()] || '';

	return (
		<Content
			currentMenu='morningwatch'
			headerTitle='MorningWatch'
			searchBox={true}
			searchPlaceholder='Search CVE, product, host'
			searchValue={searchKey}
			searchOnChange={(e) => setSearchKey(e.target.value)}
		>
			<div className={styles.intro}>
				Known vulnerabilities found on your own systems — the MorningWatch agent
				reads the software each device runs off the wire, and the C2C matches it
				against the CVE database. Each row is one vulnerability per host — a
				finding the sensor keeps seeing is shown once, with how many times and
				since when, not repeated per observation.
			</div>

			{summary && (
				<div className={styles.summary_grid}>
					<div className={styles.summary_card}>
						<span className={styles.summary_value}>{summary.total}</span>
						<span className={styles.summary_label}>Findings</span>
					</div>
					<div className={`${styles.summary_card} ${styles.crit}`}>
						<span className={styles.summary_value}>{summary.critical}</span>
						<span className={styles.summary_label}>Critical</span>
					</div>
					<div className={`${styles.summary_card} ${styles.high}`}>
						<span className={styles.summary_value}>{summary.high}</span>
						<span className={styles.summary_label}>High</span>
					</div>
					<div className={`${styles.summary_card} ${styles.kev}`}>
						<span className={styles.summary_value}>{summary.kev}</span>
						<span className={styles.summary_label}>Known Exploited</span>
					</div>
					<div className={styles.summary_card}>
						<span className={styles.summary_value}>{summary.ids != null ? summary.ids : 0}</span>
						<span className={styles.summary_label}>IDS Flags</span>
					</div>
				</div>
			)}

			<div className={styles.header_controls}>
				<div className={styles.control}>
					<SelectBox label='Watcher' value={selectedWatcher} onChange={(e) => setSelectedWatcher(e.target.value)}>
						<option value='All Watchers'>All Watchers</option>
						{watchers.map((w) => (w.length >= 2 ? (
							<option value={w[0]} key={w[0]}>{w[1]}</option>
						) : null))}
					</SelectBox>
				</div>
			</div>

			{loading ? (
				<Loader />
			) : hasError && findings.length === 0 ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={styles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={styles.supportErrorCode}>{supportErrorMessage}</code>
						</div>
					)}
				</div>
			) : (
				<div className={styles.table_container}>
					<Table
						containerStyle={{ height: '100%', flex: '1 1 auto', overflow: 'auto' }}
						tableHead={
							<tr>
								<SortableTh label='Severity' field='severity' sort={sort} />
									<SortableTh label='CVE' field='cve' sort={sort} />
									<SortableTh label='Product' field='product' sort={sort} />
									<SortableTh label='Version' field='version' sort={sort} />
									<SortableTh label='Internal Host' field='host' sort={sort} />
									<SortableTh label='Watcher' field='watcher' sort={sort} />
									<SortableTh label='Confidence' field='confidence' sort={sort} />
									<SortableTh label='Seen' field='count' sort={sort} />
									<SortableTh label='Observed' field='observedAt' sort={sort} />
							</tr>
						}
						tableRow={
							pageRows.length > 0 ? (
								pageRows.map((f, i) => (
									<tr key={`${f.matchId}-${i}`}>
										<td>
											<span className={`${styles.sevBadge} ${sevClass(f.severity)}`}>
												{f.severity}{f.kev ? ' · KEV' : ''}
											</span>
										</td>
										<td className={styles.cveCell}>
											<a
												href={`https://nvd.nist.gov/vuln/detail/${f.cve}`}
												target="_blank"
												rel="noopener noreferrer"
												title={`Open ${f.cve} on the NVD`}
											>
												{f.cve}
											</a>
										</td>
										<td>{f.product}</td>
										<td>{f.version}</td>
										<td>{f.host}</td>
										<td>{f.watcher}</td>
										<td>{f.confidence}</td>
										<td>{f.count > 1 ? `${f.count}× since ${f.firstObserved}` : '1×'}</td>
										<td>{f.observedAt}</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={9} style={{ textAlign: 'center' }}>
										No vulnerabilities found on the selected watcher yet.
									</td>
								</tr>
							)
						}
					/>
					<div className='paginationContainer'>
						<Pagination
							onPageChange={handlePageChange}
							rowsPerPage={pageLimit}
							totalPageSize={totalRows}
							pageNumber={pageNumber}
						/>
					</div>
				</div>
			)}

			{idsEvents && idsEvents.length > 0 && (
				<div style={{ marginTop: '1.5rem' }}>
					<h3 className={styles.ids_heading}>IDS-Flagged Events</h3>
					<div className={styles.intro}>
						Suricata rules the appliance flagged — many fire on version banners
						(an out-of-date OpenSSH or TLS stack), a second signal alongside the
						CVE matches above.
					</div>
					<div className={styles.table_container}>
						<Table
							containerStyle={{ height: '100%', flex: '1 1 auto', overflow: 'auto' }}
							tableHead={
								<tr>
									<SortableTh label='Severity' field='severity' sort={idsSort} />
										<SortableTh label='Signature' field='message' sort={idsSort} />
										<SortableTh label='Watcher' field='watcher' sort={idsSort} />
										<SortableTh label='Count' field='count' sort={idsSort} />
										<SortableTh label='Last Seen' field='lastSeen' sort={idsSort} />
								</tr>
							}
							tableRow={idsSort.sorted.map((e, i) => (
								<tr key={`ids-${i}`}>
									<td>
										<span className={`${styles.sevBadge} ${sevClass(e.severity)}`}>
											{e.severity || '—'}
										</span>
									</td>
									<td>{e.message}</td>
									<td>{e.watcher}</td>
									<td>{e.count > 1 ? `${e.count}×` : '1×'}</td>
									<td>{e.lastSeen}</td>
								</tr>
							))}
						/>
					</div>
				</div>
			)}
		</Content>
	);
};

export default MorningWatchScreen;
